import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormBySchema, useAPIWrapper } from 'drf-react-by-schema';

import { useAppSelector } from 'src/app/hooks';
import { appDispatch } from 'src/utils/utils';
import { setFormIsValid, setHandleSubmit, setPage } from 'src/features/pageSlice';
import { formLayout } from 'src/styles/layout';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function SignupForm() {
  const { perfil, formValues, type, id, key } = useAppSelector((state) => state.page);
  const { pageForm, loadSinglePageData, onEditModel } = useAPIWrapper();
  const navigate = useNavigate();
  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
    register,
    handleSubmit,
  } = useForm({
    mode: 'onBlur',
    resolver: pageForm?.validationSchema ? yupResolver(pageForm.validationSchema) : undefined,
  });

  const hiddenFields = useMemo(() => {
    if (id) {
      return ['first_name'];
    }
    return;
  }, [id]);
  const autoFocusField = useMemo(() => {
    if (id) {
      return 'password';
    }
    return;
  }, [id]);
  const perfilId = useMemo(() => (perfil?.id ? `${perfil?.id}` : undefined), [perfil?.id]);
  const model = useMemo(() => (key === 'signup' ? 'signup' : 'change_password'), [key]);

  useEffect(() => {
    if (!perfilId || !key || id === null) {
      return;
    }

    if (key === 'minha_senha' && (!id || id !== perfilId)) {
      navigate('/');
      return;
    }

    if (key === 'signup' && id) {
      navigate('/');
      return;
    }

    if (key === 'change_password' && !id) {
      navigate('/');
      return;
    }

    const loadAllDataAsync = async () => {
      const values = await loadSinglePageData({
        model,
        objId: id,
      });

      if (values) {
        const pageTitle = !id
          ? 'Criação de usuária do App Babaçuais'
          : id === perfilId
            ? 'Mudar sua senha'
            : `Mudar senha de ${values.first_name}`;

        // Populate the form
        reset(values);

        if (id) {
          setValue('tipo', { value: values.tipo });
        }

        appDispatch(
          setPage({
            model,
            title: pageTitle,
            formValues: values,
            submitSuccessTarget: ['signup', 'change_password'].includes(key)
              ? '/gerenciar_usuarias'
              : `/perfil/view/${id}`,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [perfilId, key, id, model]);

  useEffect(() => {
    appDispatch(
      setPage({
        formDisabled: !isDirty,
      })
    );
  }, [isDirty]);

  useEffect(() => {
    appDispatch(setFormIsValid(isValid));
  }, [isValid]);

  useEffect(() => {
    appDispatch(setHandleSubmit(handleSubmit));
  }, [handleSubmit]);

  if (!pageForm?.schema || !perfil || !formValues || !type) {
    return null;
  }

  return (
    <>
      {key === 'minha_senha' && (
        <Typography variant="h6" sx={{ mt: 5 }}>
          Escolha uma nova senha para você.
        </Typography>
      )}
      <FormBySchema
        control={control}
        register={register}
        errors={errors}
        schema={pageForm.schema}
        hiddenFields={hiddenFields}
        autoFocusField={autoFocusField}
        onEditModel={onEditModel}
        getValues={getValues}
        setValue={setValue}
        elevation={0}
        {...formLayout}
      />
    </>
  );
}
